import Button from '@/components/elements/Button';
// import useErrorMetadata from '@/presentation/hooks/useErrorMetadata';
import { TransactionState } from '@nftx/react';
import { TransactionCancelledError } from 'nftx.js';
import { Children, ReactNode, useEffect, useMemo } from 'react';
import { useTranslations } from 'use-intl';

export type Props = {
  title: ReactNode;
  description: ReactNode;
  onCancel(): void;
  onComplete(): void;
  children: ReactNode;
  customRetryAction?: ReactNode;
  showCancel?: boolean;
  currentError: any;
  currentStatus: TransactionState;
  currentIndex: number;
  retry: () => void;
};

export const ActionList = ({
  description,
  title,
  onCancel,
  onComplete,
  children,
  customRetryAction,
  showCancel = true,
  currentError,
  currentStatus,
  currentIndex,
  retry,
}: Props) => {
  const t = useTranslations('ActionList');

  const total = Children.count(children);
  const hasFailed = ['Fail', 'Exception'].includes(currentStatus);

  const hasCancelled = currentError instanceof TransactionCancelledError;
  const errorMessage = currentError?.message;

  useEffect(() => {
    if (currentError) {
      console.error(currentError);
    }
  }, [currentError]);

  useEffect(() => {
    if (currentIndex >= total) {
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  return (
    <div>
      <div className="bg-gray-50 rounded-lg">
        <h2 className="text-lg text-center mb-2 font-bold">{title}</h2>
        <h3 className="text-gray-500 text-base text-center mb-4">
          {description}
        </h3>
        {hasFailed && (
          <div className="text-center mb-4 text-orange-500 break-all overflow-hidden text-sm">
            {errorMessage}
          </div>
        )}
        {hasCancelled && (
          <div className="text-center mb-4 text-yellow-400 break-all overflow-hidden text-sm">
            {t('error.rejected')}
          </div>
        )}
        <div>
          <ul className="space-y-2">{children}</ul>
        </div>
      </div>
      {(customRetryAction || hasFailed || hasCancelled) && (
        <div className="mt-4 space-y-2">
          {customRetryAction || (
            <>
              <Button variant="primary" onClick={retry} className="w-full">
                {t('retry')}
              </Button>
              {showCancel && (
                <Button variant="ghost" onClick={onCancel} className="w-full">
                  {t('cancel')}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionList;
